import { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { en } from './en.js';
import { ja } from './ja.js';
import type { Dictionary, VariantStateKey } from './types.js';

export type Locale = 'en' | 'ja';

const DICTIONARIES: Record<Locale, Dictionary> = { en, ja };

/** `ja` is selected when `navigator.language` starts with `ja` (task brief); everything else falls back to English, the fallback locale per visual design spec section 8.2. */
export const detectLocale = (): Locale => {
  if (typeof navigator === 'undefined' || typeof navigator.language !== 'string') return 'en';
  return navigator.language.toLowerCase().startsWith('ja') ? 'ja' : 'en';
};

// A variant's wire-level state (packages/domain/src/variant/state.ts) uses
// snake_case ids; the dictionary keys are camelCase. This is the one place
// that maps between them, so every screen reads a state label the same way.
export const stateDictionaryKey = (state: string): VariantStateKey => {
  const map: Record<string, VariantStateKey> = {
    draft: 'draft',
    validated: 'validated',
    needs_review: 'needsReview',
    policy_check: 'policyCheck',
    approved: 'approved',
    scheduled: 'scheduled',
    publishing: 'publishing',
    published: 'published',
    failed: 'failed',
    canceled: 'canceled',
    held: 'held',
  };
  return map[state] ?? 'draft';
};

type I18nContextValue = { locale: Locale; setLocale: (l: Locale) => void; dict: Dictionary };

const I18nContext = createContext<I18nContextValue | null>(null);

export const I18nProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const [locale, setLocale] = useState<Locale>(() => detectLocale());
  const value = useMemo<I18nContextValue>(() => ({ locale, setLocale, dict: DICTIONARIES[locale] }), [locale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useDictionary = (): Dictionary => {
  const ctx = useContext(I18nContext);
  // Falling back to English rather than throwing lets Stamp/Counter/StateChip
  // be unit-tested in isolation (apps/web/test/stamp.test.tsx,
  // counter.test.tsx) without wrapping every render in <I18nProvider>.
  return ctx?.dict ?? en;
};

export const useLocale = (): [Locale, (l: Locale) => void] => {
  const ctx = useContext(I18nContext);
  if (!ctx) return ['en', () => {}];
  return [ctx.locale, ctx.setLocale];
};
